import { MOTIVOS_BASURA, type CalidadFila } from '@app/shared';

import { useI18n } from '../../../i18n/I18nProvider';

// ciclo-calidad-leads (R7): calidad y costo por lead bueno, por anuncio o por
// campaña. Sin título de página: la etiqueta de la tarjeta dice qué es. El
// tamaño de muestra va al lado de cada porcentaje porque con pocos leads
// calificados el número salta mucho.

const formatoMoneda = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  maximumFractionDigits: 0,
});

interface Props {
  etiqueta: string;
  columnaNombre: string;
  filas: CalidadFila[];
  total?: CalidadFila;
}

export function TablaCalidad({ etiqueta, columnaNombre, filas, total }: Props): JSX.Element {
  const es = useI18n();
  const t = es.embudo.calidad;

  const nombreDe = (f: CalidadFila): string =>
    f.id === null ? t.sinAnuncio : (f.nombre ?? t.sinNombre.replace('{id}', f.id));

  return (
    <div className="mt-4 rounded-md border border-marca-neutro bg-surface p-4 shadow-card">
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-subtle">{etiqueta}</p>
      {filas.length === 0 ? (
        <p className="py-6 text-center text-sm text-text-faint">{t.vacio}</p>
      ) : (
        // La tabla puede ser más ancha que el celular: scrollea ella, no la página.
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-xs">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-text-subtle">
                <th className="py-1 pr-3 text-left font-medium">{columnaNombre}</th>
                <th className="px-2 py-1 text-right font-medium">{t.leads}</th>
                <th className="px-2 py-1 text-right font-medium">{t.buenos}</th>
                <th className="px-2 py-1 text-right font-medium">{t.basura}</th>
                <th className="px-2 py-1 text-right font-medium">{t.sinRespuesta}</th>
                <th className="px-2 py-1 text-right font-medium">{t.sinCalificar}</th>
                <th className="px-2 py-1 text-right font-medium">{t.gasto}</th>
                <th className="py-1 pl-2 text-right font-medium">{t.costoPorLeadBueno}</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <Fila key={f.id ?? 'sin-id'} f={f} nombre={nombreDe(f)} />
              ))}
              {total && filas.length > 1 && <Fila f={total} nombre={t.total} fuerte />}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function desgloseBasura(f: CalidadFila, motivos: Record<string, string>): string {
  return MOTIVOS_BASURA.filter((m) => (f.basuraPorMotivo[m] ?? 0) > 0)
    .map((m) => `${motivos[m] ?? m}: ${String(f.basuraPorMotivo[m])}`)
    .join(' · ');
}

function Fila({ f, nombre, fuerte }: { f: CalidadFila; nombre: string; fuerte?: boolean }): JSX.Element {
  const es = useI18n();
  const t = es.embudo.calidad;
  const juzgados = f.buenos + f.basura;
  return (
    <tr
      className={fuerte ? 'border-t-2 border-border-strong font-semibold' : 'border-t border-border-subtle'}
    >
      <td className="max-w-[220px] truncate py-2 pr-3 text-text-strong" title={nombre}>
        {nombre}
      </td>
      <td className="px-2 py-2 text-right tabular-nums">{f.leads}</td>
      <td className="px-2 py-2 text-right tabular-nums text-success">
        {f.buenos}
        {f.pctBuenos !== null && <span className="ml-1 text-[11px] text-text-subtle">({f.pctBuenos}%)</span>}
      </td>
      <td
        className="px-2 py-2 text-right tabular-nums text-danger"
        title={desgloseBasura(f, es.bandeja.calidad.motivos) || undefined}
      >
        {f.basura}
        {f.pctBasura !== null && <span className="ml-1 text-[11px] text-text-subtle">({f.pctBasura}%)</span>}
      </td>
      <td className="px-2 py-2 text-right tabular-nums text-text-subtle">{f.sinRespuesta}</td>
      <td className="px-2 py-2 text-right tabular-nums text-text-subtle">{f.sinCalificar}</td>
      <td className="px-2 py-2 text-right tabular-nums">{formatoMoneda.format(f.gasto)}</td>
      <td className="py-2 pl-2 text-right tabular-nums">
        {f.costoPorLeadBueno === null ? (
          <span className="text-text-faint">{es.embudo.sinDatos}</span>
        ) : (
          <span className="font-semibold text-text-marca">{formatoMoneda.format(f.costoPorLeadBueno)}</span>
        )}
        {juzgados > 0 && (
          <span className="block text-[10px] font-normal text-text-faint">
            {t.muestra.replace('{n}', String(juzgados))}
          </span>
        )}
      </td>
    </tr>
  );
}
